import {useMemo, useRef, useState} from 'react'
import styled from 'styled-components'
import ReactGA from 'react-ga4'
import {useNavigate, useParams} from 'react-router-dom'
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Tooltip,
} from '@mui/material'
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined'
import StorageOutlinedIcon from '@mui/icons-material/StorageOutlined'
import AddOutlinedIcon from '@mui/icons-material/AddOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {getCurrentColors} from '../../utils/colors'
import LeftBar from '../home/components/leftBar'
import {
    collectPending,
    countFiles,
    createIndex,
    DriveFile,
    DriveFolder,
    filesForIndex,
    KBS,
    PINECONE_INDEXES,
    addFilesToFolder,
    applyPendingToTree,
    mapFileInTree,
} from './mockData'
import {
    ConfirmDialog,
    DriveTree,
    filesToDriveFiles,
    IndexChangeItem,
    SyncPill,
} from './shared'

const COLORS = getCurrentColors()

interface PendingIndexChange extends IndexChangeItem {
    fileId: string
    indexId: string
}

function allFolderIds(node: DriveFolder): string[] {
    return [
        node.id,
        ...node.children.flatMap((c) =>
            c.kind === 'folder' ? allFolderIds(c) : []
        ),
    ]
}

const Page = styled.div`
    display: flex;
    height: 100vh;
    overflow: hidden !important;
`

const Main = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
    background: #f7f9fa;
`

const Scroll = styled.div`
    flex: 1;
    overflow-y: auto;
`

const Wrap = styled.div`
    max-width: 1080px;
    margin: 0 auto;
    padding: 40px 26px 60px;
`

const BackLink = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    color: #8a969c;
    font-weight: 500;
    margin-bottom: 16px;
    border: none;
    background: none;
    cursor: pointer;
    padding: 0;

    &:hover {
        color: #0c1f29;
    }

    svg {
        font-size: 15px;
    }
`

const Header = styled.div`
    background: #ffffff;
    border: 1px solid #e6eaec;
    border-radius: 12px;
    padding: 18px 20px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    gap: 14px;
    flex-wrap: wrap;
`

const HeaderIcon = styled.span`
    width: 48px;
    height: 48px;
    border-radius: 13px;
    background: linear-gradient(
        120deg,
        ${COLORS.primariaEscura},
        ${COLORS.secundariaEscura}
    );
    display: grid;
    place-items: center;
    flex-shrink: 0;

    svg {
        color: #fff;
    }
`

const HeaderName = styled.div`
    font-weight: 600;
    font-size: 18px;
    color: #0c1f29;
`

const HeaderMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: #8a969c;
    margin-top: 4px;
    flex-wrap: wrap;
`

const HeaderRight = styled.div`
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 9px;
`

const SectionTitle = styled.h2`
    font-size: 15px;
    color: #0c1f29;
    margin: 0 0 12px;
`

const TreeBox = styled.div`
    background: #ffffff;
    border: 1px solid #e6eaec;
    border-radius: 12px;
    padding: 10px;
    margin-bottom: 12px;
`

const TargetHint = styled.div`
    font-size: 12px;
    color: #8a969c;
    margin-bottom: 20px;
`

const IndexSummary = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    width: 100%;
`

const LinkedFileList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`

const LinkedFileRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 8px 10px;
    background: #f7f9fa;
    border-radius: 8px;
    font-size: 13px;
    color: #33454e;

    span {
        color: #8a969c;
        font-size: 12px;
        flex-shrink: 0;
    }
`

const HistoryName = styled.span`
    font-family: monospace;
    color: #0c1f29;
    font-weight: 600;
`

const HistoryDate = styled.span`
    font-variant-numeric: tabular-nums;
    color: #8a969c;
`

const HistoryDetail = styled.span`
    color: #8a969c;
    margin-left: auto;
    font-size: 12px;
`

const EmptyState = styled.div`
    padding: 20px 0;
    text-align: center;
    color: #8a969c;
    font-size: 13px;
`

export default function BasesDeDadosDetalhe() {
    const navigate = useNavigate()
    const {nome} = useParams<{nome: string}>()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const kb = useMemo(() => KBS.find((k) => k.name === nome), [nome])

    const [tree, setTree] = useState<DriveFolder>(() =>
        kb ? (JSON.parse(JSON.stringify(kb.root)) as DriveFolder) : {id: 'empty', kind: 'folder', name: '', children: []}
    )
    const [expandedIds, setExpandedIds] = useState<Set<string>>(
        () => new Set(allFolderIds(tree))
    )
    const [selectedFolderId, setSelectedFolderId] = useState(tree.id)
    const [indexes, setIndexes] = useState(
        () => PINECONE_INDEXES[nome ?? ''] ?? []
    )
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [indexChanges, setIndexChanges] = useState<PendingIndexChange[]>([])

    ReactGA.send({
        hitType: 'pageview',
        page: `/bases-de-dados/${nome}`,
        title: 'Bases de dados - detalhe',
    })

    if (!kb) {
        return (
            <Page>
                <LeftBar />
                <Main>
                    <Scroll>
                        <Wrap>
                            <BackLink
                                onClick={() => navigate('/bases-de-dados')}
                            >
                                <ArrowBackOutlinedIcon /> Voltar para bases
                            </BackLink>
                            <p>Índice não encontrado.</p>
                        </Wrap>
                    </Scroll>
                </Main>
            </Page>
        )
    }

    const currentKb = kb
    const {uploads, removals} = collectPending(tree)
    const pendingCount =
        uploads.length + removals.length + indexChanges.length
    const selectedFolderName =
        selectedFolderId === tree.id
            ? tree.name
            : findFolderName(tree, selectedFolderId) ?? tree.name

    function handleToggleExpand(id: string) {
        setExpandedIds((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    function handleAddFiles(fileList: FileList | null) {
        if (!fileList || fileList.length === 0) return
        const newFiles = filesToDriveFiles(fileList)
        setTree(
            (prev) => addFilesToFolder(prev, selectedFolderId, newFiles) as DriveFolder
        )
    }

    function handleToggleRemove(file: DriveFile) {
        setTree((prev) => {
            const updated = mapFileInTree(prev, file.id, (f) => {
                if (f.pending === 'upload') return null
                return {
                    ...f,
                    pending: f.pending === 'remove' ? undefined : 'remove',
                }
            })
            return (updated ?? prev) as DriveFolder
        })
    }

    function handleConfirmUpdate() {
        const finalTree = applyPendingToTree(tree) as DriveFolder

        currentKb.root = finalTree
        currentKb.lastSync = 'ok'
        currentKb.lastSyncLabel = 'Sincronizado'

        setTree(finalTree)
        setIndexChanges([])
        setConfirmOpen(false)
    }

    function handleToggleFileIndex(file: DriveFile, indexId: string) {
        const willAdd = !file.indexIds.includes(indexId)
        const nextIndexIds = willAdd
            ? [...file.indexIds, indexId]
            : file.indexIds.filter((id) => id !== indexId)

        setTree((prev) => {
            const updated = mapFileInTree(prev, file.id, (f) => ({
                ...f,
                indexIds: nextIndexIds,
            }))
            return (updated ?? prev) as DriveFolder
        })

        // Igual upload/remoção: fica pendente até confirmar o "Atualizar".
        // Alternar de novo o mesmo par arquivo/índice desfaz a pendência.
        setIndexChanges((prev) => {
            const existingIdx = prev.findIndex(
                (c) => c.fileId === file.id && c.indexId === indexId
            )
            if (existingIdx !== -1) {
                return prev.filter((_, i) => i !== existingIdx)
            }
            const indexName =
                indexes.find((i) => i.id === indexId)?.name ?? indexId
            return [
                ...prev,
                {
                    fileId: file.id,
                    indexId,
                    fileName: file.name,
                    indexName,
                    added: willAdd,
                },
            ]
        })
    }

    function handleCreateIndex() {
        const updated = createIndex(currentKb.name)
        setIndexes(updated)
    }

    return (
        <Page>
            <LeftBar />
            <Main>
                <Scroll>
                    <Wrap>
                        <BackLink onClick={() => navigate('/bases-de-dados')}>
                            <ArrowBackOutlinedIcon /> Voltar para bases
                        </BackLink>

                        <Header>
                            <HeaderIcon>
                                <StorageOutlinedIcon />
                            </HeaderIcon>
                            <div>
                                <HeaderName>{kb.name}</HeaderName>
                                <HeaderMeta>
                                    <SyncPill status={kb.lastSync}>
                                        {kb.lastSyncLabel}
                                    </SyncPill>
                                    · {countFiles(tree)} documentos ·{' '}
                                    {kb.drivePath}
                                </HeaderMeta>
                            </div>
                            <HeaderRight>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    hidden
                                    onChange={(e) => {
                                        handleAddFiles(e.target.files)
                                        e.target.value = ''
                                    }}
                                />
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<UploadFileOutlinedIcon />}
                                    onClick={() =>
                                        fileInputRef.current?.click()
                                    }
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '9px',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Adicionar arquivos
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    startIcon={<AddOutlinedIcon />}
                                    onClick={handleCreateIndex}
                                    sx={{
                                        textTransform: 'none',
                                        borderRadius: '9px',
                                        fontWeight: 600,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    Novo índice
                                </Button>
                                <Tooltip
                                    title={
                                        pendingCount === 0
                                            ? 'Nenhuma alteração pendente'
                                            : ''
                                    }
                                >
                                    <span>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            disabled={pendingCount === 0}
                                            startIcon={<SyncOutlinedIcon />}
                                            onClick={() =>
                                                setConfirmOpen(true)
                                            }
                                            sx={{
                                                textTransform: 'none',
                                                borderRadius: '9px',
                                                fontWeight: 600,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Atualizar
                                            {pendingCount > 0
                                                ? ` (${pendingCount})`
                                                : ''}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </HeaderRight>
                        </Header>

                        <SectionTitle>Índices gerados</SectionTitle>
                        {indexes.length === 0 ? (
                            <EmptyState>
                                Nenhum índice criado ainda para esta KB.
                            </EmptyState>
                        ) : (
                            indexes.map((idx) => {
                                const linkedFiles = filesForIndex(
                                    tree,
                                    idx.id
                                )
                                return (
                                    <Accordion
                                        key={idx.id}
                                        disableGutters
                                        sx={{
                                            marginBottom: '10px',
                                            borderRadius: '10px !important',
                                            border: '1px solid #e6eaec',
                                            boxShadow: 'none',
                                            '&::before': {display: 'none'},
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                        >
                                            <IndexSummary>
                                                <SyncPill status={idx.status}>
                                                    {idx.statusLabel}
                                                </SyncPill>
                                                <HistoryName>
                                                    {idx.name}
                                                </HistoryName>
                                                <HistoryDate>
                                                    {idx.date}
                                                </HistoryDate>
                                                <span>{idx.triggeredBy}</span>
                                                <HistoryDetail>
                                                    {linkedFiles.length}{' '}
                                                    arquivo(s) vinculado(s)
                                                </HistoryDetail>
                                            </IndexSummary>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            {linkedFiles.length === 0 ? (
                                                <EmptyState>
                                                    Nenhum documento
                                                    vinculado a este índice
                                                    ainda. Use o botão de
                                                    associar em cada arquivo
                                                    abaixo.
                                                </EmptyState>
                                            ) : (
                                                <LinkedFileList>
                                                    {linkedFiles.map(
                                                        (f) => (
                                                            <LinkedFileRow
                                                                key={f.id}
                                                            >
                                                                {f.name}
                                                                <span>
                                                                    {f.size}
                                                                </span>
                                                            </LinkedFileRow>
                                                        )
                                                    )}
                                                </LinkedFileList>
                                            )}
                                        </AccordionDetails>
                                    </Accordion>
                                )
                            })
                        )}

                        <SectionTitle>Documentos</SectionTitle>
                        <TreeBox>
                            <DriveTree
                                node={tree}
                                expandedIds={expandedIds}
                                onToggleExpand={handleToggleExpand}
                                selectedFolderId={selectedFolderId}
                                onSelectFolder={setSelectedFolderId}
                                onToggleRemove={handleToggleRemove}
                                indexes={indexes}
                                onToggleIndex={handleToggleFileIndex}
                            />
                        </TreeBox>
                        <TargetHint>
                            Clique em uma pasta para escolher onde os novos
                            arquivos serão adicionados. Destino atual:{' '}
                            <b>{selectedFolderName}</b>.
                        </TargetHint>
                    </Wrap>
                </Scroll>
            </Main>

            <ConfirmDialog
                open={confirmOpen}
                title="Confirmar atualização"
                uploads={uploads}
                removals={removals}
                indexChanges={indexChanges}
                onCancel={() => setConfirmOpen(false)}
                onConfirm={handleConfirmUpdate}
                confirmLabel="Confirmar atualização"
            />
        </Page>
    )
}

function findFolderName(node: DriveFolder, id: string): string | undefined {
    if (node.id === id) return node.name
    for (const child of node.children) {
        if (child.kind === 'folder') {
            const found = findFolderName(child, id)
            if (found) return found
        }
    }
    return undefined
}
